import {BlobRef} from '@atproto/api'
import {CID} from 'multiformats/cid'

import {logger} from '#/logger'
import {type DocumentMaterialRef} from './material'
import {
  DOCUMENT_EMBED_RECORD_TYPE,
  type DocumentAudience,
  type DocumentBlobRef,
  type DocumentEmbedRecord,
  normalizeDocumentAudience,
} from './types'

export type DocumentPublishData = {
  blob: DocumentBlobRef
  mimeType: string
  title: string
  pageCount: number
  fileSize: number
}

/**
 * Rebuilds a `BlobRef` from the JSON blob returned by the document upload.
 * Keeping this a real `BlobRef` matters: the record is hashed locally to
 * compute the post CID, and only `BlobRef#ipld()` produces the CID link that
 * the server ends up storing.
 */
function toBlobRef(blob: DocumentBlobRef): BlobRef | DocumentBlobRef {
  try {
    return new BlobRef(CID.parse(blob.ref.$link), blob.mimeType, blob.size)
  } catch (e) {
    logger.error('Failed to parse document blob cid', {safeMessage: e})
    return blob
  }
}

/**
 * Builds the `app.bsky.embed.document` record for a finished upload. Kept
 * separate from the API layer so it can be unit tested.
 */
export function buildDocumentEmbedRecord({
  document,
  alt,
  audience,
  material,
}: {
  document: DocumentPublishData
  alt?: string
  /** Declared at publish time; omitted when the author has not chosen one. */
  audience?: DocumentAudience
  /**
   * Where the document's page material lives, and what it holds.
   *
   * Omitted entirely when there is none, which is what keeps a plain PDF post
   * byte-identical to the post this feature shipped before: no key, no counts, no
   * address — nothing for a reader to look up and nothing for a badge to draw.
   */
  material?: DocumentMaterialRef
}): DocumentEmbedRecord {
  const normalized = normalizeDocumentAudience(
    audience?.level,
    audience?.subject,
  )
  return {
    $type: DOCUMENT_EMBED_RECORD_TYPE,
    blob: toBlobRef(document.blob) as DocumentBlobRef,
    mimeType: document.mimeType,
    title: document.title,
    pageCount: document.pageCount,
    fileSize: document.fileSize,
    alt: alt?.trim() ? alt.trim() : undefined,
    // Both codes or neither: a half-declared audience is not an audience.
    level: normalized?.level,
    subject: normalized?.subject,
    ...(material
      ? {materialUri: material.uri, materialPages: material.pages}
      : {}),
  }
}
